// Typed event bus (§23.3). DOM-free. Listeners are isolated: one that throws does not stop the
// others; its error is rethrown asynchronously so it still surfaces as an uncaught error.
// Additive: 'sim' events re-dispatched by a replay (§23.6) carry `replay: true`, so juice and audio
// replay them while the kill feed, stats and the Tapestry skip them.
import type { SimEvent } from '@shared/events';
import type { MatchEndDTO, RoomStateDTO, RoundEndDTO, RoundInitDTO } from '@shared/protocol/messages';
import type { Profile } from '../profile/Profile';
import type { Settings } from '../profile/settings';
import type { QualityTier } from '../render/Renderer';
import type { ScreenId } from '../ui/contracts';

export interface BusEvents {
  'sim': { ev: SimEvent; local: false; replay?: boolean };
  'sim:local': { ev: SimEvent; local: true };
  'net:status': { state: 'offline' | 'connecting' | 'online' | 'reconnecting'; attempt: number };
  'room': RoomStateDTO;
  'joined': { code: string; slot: number; spectator: boolean; isHost: boolean };
  'round:init': RoundInitDTO;
  'round:end': RoundEndDTO;
  'match:end': MatchEndDTO;
  'toast': { text: string; kind: string };
  'error': { code: string; msg: string };
  'replay:start': { kind: 'instant' | 'bonkcam' | 'potm' };
  'replay:end': { kind: 'instant' | 'bonkcam' | 'potm' };
  'input:device': 'kbm' | 'pad' | 'touch';
  'input:nav': { action: 'up' | 'down' | 'left' | 'right' | 'confirm' | 'back' | 'tabL' | 'tabR' | 'pause' | 'scoreboard' };
  'settings': Settings;
  'profile': Profile;
  'quality': QualityTier;
  'audio:unlocked': void;
  'ui:screen': ScreenId;
  'challenge:progress': { id: number; progress: number; goal: number; done: boolean };
}
export interface Bus { on<K extends keyof BusEvents>(k: K, fn: (p: BusEvents[K]) => void): () => void; emit<K extends keyof BusEvents>(k: K, p: BusEvents[K]): void }

type AnyListener = (p: unknown) => void;

class EventBus implements Bus {
  // Copy-on-write arrays: emit iterates a stable snapshot without allocating, and listeners may
  // subscribe or unsubscribe while an event is being dispatched.
  private readonly listeners = new Map<keyof BusEvents, readonly AnyListener[]>();

  on<K extends keyof BusEvents>(k: K, fn: (p: BusEvents[K]) => void): () => void {
    const listener = fn as AnyListener;
    this.listeners.set(k, [...(this.listeners.get(k) ?? []), listener]);
    let subscribed = true;
    return () => {
      if (!subscribed) return;
      subscribed = false;
      const list = this.listeners.get(k) ?? [];
      const i = list.indexOf(listener);
      if (i >= 0) this.listeners.set(k, [...list.slice(0, i), ...list.slice(i + 1)]);
    };
  }

  emit<K extends keyof BusEvents>(k: K, p: BusEvents[K]): void {
    const list = this.listeners.get(k);
    if (!list) return;
    for (const fn of list) {
      try {
        fn(p);
      } catch (err) {
        queueMicrotask(() => {
          throw err;
        });
      }
    }
  }
}

export function createBus(): Bus {
  return new EventBus();
}

/**
 * Subscribes to both sim channels; `local` is true for locally predicted events and `replay` for
 * events a replay re-dispatches.
 */
export function onSim(bus: Bus, fn: (ev: SimEvent, local: boolean, replay: boolean) => void): () => void {
  const offServer = bus.on('sim', (p) => fn(p.ev, false, p.replay === true));
  const offLocal = bus.on('sim:local', (p) => fn(p.ev, true, false));
  return () => {
    offServer();
    offLocal();
  };
}
